/**
 * Docking rules for the Request Correspondence panel (#1069).
 *
 * The panel DOCKS beside the request (pushing content left and shrinking it)
 * on wide web viewports, and falls back to an overlay everywhere else. Since
 * UI-4 the docked panel is also DRAGGABLE, so its width is a range rather than
 * a single number and the arithmetic below has to hold across that whole range.
 *
 * ⚠️ WHY THE THRESHOLD IS 1280 AND NOT 1200
 *
 * `spacing.formMaxWidth` (880) is derived from the request layout breakpoint at
 * `REQUEST_FORM_MOBILE_LAYOUT_MAX_WIDTH` = 1200, where the left rail swaps
 * 252px <-> 60px. Crossing that breakpoint changes the available pane by 192px
 * while the viewport changes by 1px, which is why the cap has to bind on both
 * sides — see `.claude/rules/responsive-ui.md` and `form-max-width.test.ts`.
 *
 * Docking only above 1280 means **the docked panel is never on screen in the
 * region where the rail swaps**, so the two mechanisms cannot interact and the
 * documented "fields get wider as the window gets narrower" inversion cannot be
 * reintroduced through this feature.
 *
 * The number is derived, not chosen:
 *
 *   252 left rail (REQUEST_FORM_SIDEBAR_EXPANDED_WIDTH_WEB)
 * + 600 content floor (below this the two-column field grid is not usable)
 * +  40 Request Detail page padding (20 per side)
 * +  15 classic scrollbar reserve
 * + 360 panel MINIMUM (DOCK_PANEL_MIN_WIDTH)
 * = 1267 -> 1280
 *
 * ⚠️ THE 400-VERSUS-360 DISCREPANCY, AND WHY THE THRESHOLD DID NOT MOVE
 *
 * That derivation uses the panel MINIMUM (360) while `DOCK_PANEL_WIDTH` is 400,
 * and before UI-4 the panel opened at a flat 400 with nothing clamping it. So
 * at 1280 the form column was 573px — 27px BELOW the floor the whole derivation
 * exists to protect. Two numbers appeared to disagree.
 *
 * **They never did. The derivation was right; applying the default width
 * unclamped was wrong.** The threshold promises the panel can always reach its
 * 360px minimum with the floor intact, and it delivers exactly that:
 * `maxPanelWidth(1280)` is 373, comfortably above 360.
 *
 * UI-4 fixes it at the source instead of moving the threshold. Every width now
 * goes through {@link clampPanelWidth}, including the default, so the panel
 * opens at `min(400, maxPanelWidth(viewport))`:
 *
 *   viewport 1280  ->  opens at 373, column exactly 600
 *   viewport 1307  ->  opens at 400, column exactly 600
 *   viewport 1440  ->  opens at 400, column 733
 *
 * Raising the threshold to 1320 to make a flat 400 always fit was considered
 * and rejected. It would have deleted the 1280–1319 band (including 1280
 * itself, the conventional desktop width and this repo's own E2E viewport),
 * contradicted the spec's "crossing the threshold is unchanged behaviour", and
 * spent an Ask-First safety constant to buy something the clamp already gives.
 * Clamping the default is the same mechanism that already handles "the viewport
 * shrank under a wide panel" — using it here too makes the design consistent
 * with itself.
 *
 * `formMaxWidth` is a CAP, not a width, so it does not need re-deriving: once
 * the panel is open the available column falls below 880 and the cap simply
 * stops binding, which is the ordinary shrink-to-fit we want.
 */
import { REQUEST_FORM_SIDEBAR_EXPANDED_WIDTH_WEB } from '../../constants/requestFormLayout';

/**
 * Request Detail page padding, per side (`styles.content` is a static 20).
 * Request Detail is the tighter of the two request screens and the only one
 * that can host the dock, so it is the case to derive against.
 */
export const DOCK_PAGE_PADDING_PER_SIDE = 20;

/**
 * Classic vertical scrollbar reserve. Measured, not assumed: the step scroller
 * reports offsetWidth 948 / clientWidth 933 at viewport 1200, because Request
 * Detail sets `showsVerticalScrollIndicator={true}`. Modelling padding alone
 * has already produced a wrong answer here once (#1438).
 */
export const DOCK_SCROLLBAR_RESERVE = 15;

/**
 * Everything on the row that is neither the panel nor the form column.
 * 252 + 20*2 + 15 = 307.
 */
export const DOCK_CHROME_WIDTH =
  REQUEST_FORM_SIDEBAR_EXPANDED_WIDTH_WEB +
  DOCK_PAGE_PADDING_PER_SIDE * 2 +
  DOCK_SCROLLBAR_RESERVE;

/** Narrowest content column we are willing to leave the form when docked. */
export const DOCK_CONTENT_FLOOR = 600;

/**
 * Width the panel PREFERS when it opens — the "mid width". Unchanged by UI-4,
 * but no longer applied raw: it is a request passed through
 * {@link clampPanelWidth}, so on a narrow docking viewport the panel opens at
 * whatever fits instead of overrunning the content floor.
 */
export const DOCK_PANEL_WIDTH = 400;

/** Narrowest the user may drag the panel before the composer feels cramped. */
export const DOCK_PANEL_MIN_WIDTH = 360;

/**
 * Minimum viewport width at which the panel docks instead of overlaying.
 * Derived above from the panel MINIMUM — do not hand-edit.
 */
export const DOCK_MIN_VIEWPORT_WIDTH = 1280;

/** Keyboard resize increment for the drag handle (Shift multiplies it). */
export const DOCK_RESIZE_STEP = 16;

/** Shift+arrow multiplier, so a keyboard user can cross the range quickly. */
export const DOCK_RESIZE_COARSE_MULTIPLIER = 4;

/**
 * Width left to the form column beside a docked panel of `panelWidth`.
 *
 * This is the quantity every constraint in this file is about. It must never
 * be below {@link DOCK_CONTENT_FLOOR}, and it must never grow as the viewport
 * shrinks — that inversion is the user-visible pathology of #1438.
 */
export const formColumnWidth = (
  viewportWidth: number,
  panelWidth: number,
): number => viewportWidth - DOCK_CHROME_WIDTH - panelWidth;

/**
 * Widest panel this viewport can afford while still leaving the form its floor.
 *
 * DERIVED, never a constant. With a fixed panel one number sufficed; draggable
 * makes the constraint a relationship, and any hard-coded maximum would be
 * either wrong at the threshold or pointlessly mean at 1920. It therefore has
 * to be recomputed whenever the viewport changes.
 *
 * Floored, so a fractional viewport can never round the column below the floor.
 */
export const maxPanelWidth = (viewportWidth: number): number =>
  Math.floor(viewportWidth - DOCK_CHROME_WIDTH - DOCK_CONTENT_FLOOR);

/**
 * Clamp a requested panel width into the range this viewport allows.
 *
 * Total function: any input, any viewport, always returns a usable number.
 *
 * At every viewport where the panel actually docks (>= {@link
 * DOCK_MIN_VIEWPORT_WIDTH}) the returned width leaves the form column at or
 * above {@link DOCK_CONTENT_FLOOR}, and is never below {@link
 * DOCK_PANEL_MIN_WIDTH}. Both bounds hold together there — the guard test
 * proves the two can never conflict in the docking range.
 *
 * Below the docking threshold the bounds DO eventually conflict, and **the
 * maximum wins**: "never breach the content floor" is the absolute constraint,
 * where 360 is only a comfort floor. Narrower still and the maximum goes
 * negative, so the result is held at zero rather than returning a negative
 * width. None of that is reachable while docked; it exists so the function has
 * no undefined corner.
 */
export const clampPanelWidth = (
  panelWidth: number,
  viewportWidth: number,
): number => {
  const max = maxPanelWidth(viewportWidth);
  if (!Number.isFinite(max)) return DOCK_PANEL_MIN_WIDTH;
  if (max <= DOCK_PANEL_MIN_WIDTH) return Math.max(0, max);
  const requested = Math.round(panelWidth);
  if (!Number.isFinite(requested)) return DOCK_PANEL_MIN_WIDTH;
  return Math.min(Math.max(requested, DOCK_PANEL_MIN_WIDTH), max);
};

/**
 * Whether the panel should dock at this viewport.
 *
 * Native never docks: there is no honest way to show a 400px panel and an
 * editable form on a phone, and a split view would make both unusable.
 */
export const shouldDock = (viewportWidth: number, isWeb: boolean): boolean =>
  isWeb && viewportWidth >= DOCK_MIN_VIEWPORT_WIDTH;
